import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin, Clock, Mail, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import LevelDot from "@/components/levels/LevelDot";
import ColorKey from "@/components/levels/ColorKey";
import { DOT_COLORS, earlyBirdOpen } from "@/data/skillGuide";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { LandingPageConfig } from "@/data/landingPages";
import { trackEvent } from "@/lib/tracking";
import { withAttribution } from "@/lib/outbound";
import { SITE, MAP_EMBED_URL } from "@/lib/site";
import { facilitySrcSet } from "@/lib/responsiveImage";
import { heroBlur } from "@/data/heroBlur";

const ADDRESS_LINE = "160 Van Brunt Street, Brooklyn, NY 11231";
const DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=Velto+Pickleball+Club+160+Van+Brunt+Street+Brooklyn+NY+11231";
const MAP_EMBED = MAP_EMBED_URL;

/** Shared geometry for the paired hero CTAs — see renderCta below. */
const CTA_SIZING =
  "font-sweet-bold uppercase tracking-wider px-8 py-6 text-base border-2";

type Props = { config: LandingPageConfig };

function LazyMap() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (show || !ref.current) return;
    const el = ref.current;
    // No IntersectionObserver (very old browser, or a shimmed environment)
    // would otherwise leave the placeholder box empty forever.
    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show]);
  return (
    <div
      ref={ref}
      className="rounded-lg overflow-hidden border border-[hsl(var(--velto-forest))]/10 aspect-[4/3] md:aspect-auto md:min-h-[320px] bg-[hsl(var(--velto-forest))]/5"
    >
      {show && (
        <iframe
          title="Map showing Velto Pickleball Club at 160 Van Brunt Street, Brooklyn"
          src={MAP_EMBED}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="w-full h-full border-0"
        />
      )}
    </div>
  );
}

const LandingPage = ({ config }: Props) => {
  const canonical = `${SITE}${config.slug}`;

  // Hero blur-up: real <img> fades in over an inline blurred placeholder so the
  // loading state is the photo, never grey. Catch loads that finish before
  // hydration attaches onLoad via .complete on mount.
  const heroImgRef = useRef<HTMLImageElement>(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const heroBlurUri = heroBlur[config.heroImage];
  const heroBlurStyle = heroBlurUri
    ? {
        backgroundImage: `url(${heroBlurUri})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: "blur(24px)",
        transform: "scale(1.1)",
      }
    : undefined;
  useEffect(() => {
    if (heroImgRef.current?.complete) setHeroLoaded(true);
  }, []);

  useEffect(() => {
    trackEvent("landing_page_view", { page: config.slug });
  }, [config.slug]);

  const breadcrumbJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: config.breadcrumb.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.label,
        item: b.href ? `${SITE}${b.href}` : canonical,
      })),
    }),
    [config.breadcrumb, canonical],
  );

  const faqJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: config.faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    }),
    [config.faqs],
  );

  const serviceJsonLd = useMemo(() => {
    // Parse first $NN out of pricing strings like "$80/hr" or "$25 drop-in"
    const offers = config.pricing?.items
      ?.map((it) => {
        const m = it.price.match(/\$(\d+(?:\.\d{1,2})?)/);
        const offer: Record<string, unknown> = {
          "@type": "Offer",
          name: it.label,
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          url: canonical,
        };
        if (m) offer.price = m[1];
        if (it.note) offer.description = it.note;
        return offer;
      })
      .filter(Boolean);

    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: config.h1,
      description: config.metaDescription,
      url: canonical,
      serviceType: config.h1,
      areaServed: [
        { "@type": "City", name: "Brooklyn" },
        { "@type": "City", name: "New York City" },
      ],
      provider: {
        "@type": "LocalBusiness",
        name: "Velto Pickleball Club",
        url: SITE,
        telephone: "+1-516-968-5204",
        email: "support@veltopb.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "160 Van Brunt Street",
          addressLocality: "Brooklyn",
          addressRegion: "NY",
          postalCode: "11231",
          addressCountry: "US",
        },
      },
      ...(offers && offers.length
        ? { offers: { "@type": "AggregateOffer", priceCurrency: "USD", offers } }
        : {}),
    };
  }, [config, canonical]);

  // Preserve UTMs and gclid through outbound CTAs so PPC attribution survives.
  const primaryHref = withAttribution(config.primaryCta.href);
  const secondaryHref = config.secondaryCta
    ? withAttribution(config.secondaryCta.href)
    : undefined;

  const handleCta = (
    eventName: string,
    href: string,
    external?: boolean,
    extra?: Record<string, unknown>,
  ) => {
    trackEvent(eventName, {
      page: config.slug,
      destination: href,
      external: !!external,
      ...(extra || {}),
    });
  };

  const renderCta = (
    cta: LandingPageConfig["primaryCta"],
    variant: "primary" | "secondary",
    href: string,
  ) => {
    // One sizing source for both variants. Button's `size: default` bakes in
    // h-10 (40px), which is shorter than the 48px of vertical padding these
    // CTAs apply — so with box-sizing: border-box the height collapses to
    // padding + border, and the outlined variant's 2px border made it 4px
    // taller than the filled one. Both now carry border-2 (transparent on the
    // filled variant) so the border box matches exactly.
    const className = `${CTA_SIZING} ${
      variant === "primary"
        ? "bg-[hsl(var(--velto-copper))] hover:bg-[hsl(var(--velto-copper))]/90 text-white border-transparent"
        : "bg-transparent border-[hsl(var(--velto-forest))] text-[hsl(var(--velto-forest))] hover:bg-[hsl(var(--velto-forest))]/10"
    }`;

    const onClick = () => handleCta(cta.eventName, href, cta.external);

    if (cta.external) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
          <Button className={className}>{cta.label}</Button>
        </a>
      );
    }
    if (href.startsWith("#")) {
      return (
        <a href={href} onClick={onClick}>
          <Button className={className}>{cta.label}</Button>
        </a>
      );
    }
    return (
      <Link to={href} onClick={onClick}>
        <Button className={className}>{cta.label}</Button>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--velto-beige))] text-[hsl(var(--velto-forest))]">
      <Helmet>
        <title>{config.metaTitle}</title>
        <meta name="description" content={config.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={config.metaTitle} />
        <meta property="og:description" content={config.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${SITE}${config.heroImage || "/og-default.jpg"}`} />
        <meta property="og:site_name" content="Velto Pickleball Club" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.metaTitle} />
        <meta name="twitter:description" content={config.metaDescription} />
        <meta name="twitter:image" content={`${SITE}${config.heroImage || "/og-default.jpg"}`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
      </Helmet>

      <Header />

      <main className="pt-banner-5 pb-24">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="container mx-auto px-4 md:px-6 pt-6 text-sm font-sweet text-[hsl(var(--velto-forest))]/70"
        >
          <ol className="flex flex-wrap items-center gap-1">
            {config.breadcrumb.map((b, i) => {
              const isLast = i === config.breadcrumb.length - 1;
              return (
                <li key={`${b.label}-${i}`} className="flex items-center gap-1">
                  {b.href && !isLast ? (
                    <Link
                      to={b.href}
                      className="hover:text-[hsl(var(--velto-copper))] underline-offset-4 hover:underline"
                    >
                      {b.label}
                    </Link>
                  ) : (
                    <span aria-current={isLast ? "page" : undefined}>{b.label}</span>
                  )}
                  {!isLast && <ChevronRight className="h-3.5 w-3.5 opacity-60" />}
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Hero */}
        <section className="container mx-auto px-4 md:px-6 pt-8 pb-12 md:pt-12 md:pb-16">
          <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h1 className="font-instrument text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-5">
                {config.h1}
              </h1>
              <p className="font-sweet text-lg md:text-xl text-[hsl(var(--velto-forest))]/80 mb-8 max-w-xl">
                {config.subhead}
              </p>
              <div className="flex flex-wrap gap-3">
                {renderCta(config.primaryCta, "primary", primaryHref)}
                {config.secondaryCta &&
                  secondaryHref &&
                  renderCta(config.secondaryCta, "secondary", secondaryHref)}
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-sm font-sweet text-[hsl(var(--velto-forest))]/70">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> Red Hook, Brooklyn
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> Open daily 6AM–12AM
                </span>
              </div>
            </div>
            {/* Blur-up: behind layer is always on (never grey, even pre-JS); the
                real <img> keeps opacity:1 so it stays the LCP element; the on-top
                layer fades out on load (instant under prefers-reduced-motion). */}
            <div className="relative rounded-xl overflow-hidden shadow-lg border border-[hsl(var(--velto-forest))]/10">
              {heroBlurStyle && (
                <div aria-hidden="true" className="absolute inset-0 z-0" style={heroBlurStyle} />
              )}
              <img
                ref={heroImgRef}
                src={config.heroImage}
                srcSet={facilitySrcSet(config.heroImage)}
                sizes="(min-width: 768px) 50vw, calc(100vw - 2rem)"
                alt={config.heroImageAlt}
                width={1200}
                height={800}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                onLoad={() => setHeroLoaded(true)}
                className="relative z-10 w-full h-[280px] md:h-[420px] object-cover"
              />
              {heroBlurStyle && (
                <div
                  aria-hidden="true"
                  className={`absolute inset-0 z-20 pointer-events-none transition-opacity duration-700 motion-reduce:transition-none ${heroLoaded ? "opacity-0" : "opacity-100"}`}
                  style={heroBlurStyle}
                />
              )}
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="container mx-auto px-4 md:px-6 pb-10">
          <div className="max-w-3xl space-y-4 font-sweet text-base md:text-lg leading-relaxed text-[hsl(var(--velto-forest))]/90">
            {config.intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </section>

        {/* Features */}
        <section id="features" className="container mx-auto px-4 md:px-6 pb-14">
          <h2 className="font-instrument text-3xl md:text-4xl mb-8">
            What you get at Velto
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {config.features.map((f) => (
              <div
                key={f.title}
                className="bg-white/60 backdrop-blur-sm border border-[hsl(var(--velto-forest))]/10 rounded-lg p-5"
              >
                <h3 className="font-instrument text-xl mb-2">{f.title}</h3>
                <p className="font-sweet text-[hsl(var(--velto-forest))]/80 text-sm md:text-base leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Schedule color key (optional) */}
        {config.colorKey && (
          <section id="color-key" className="container mx-auto px-4 md:px-6 pb-14">
            <h2 className="font-instrument text-3xl md:text-4xl mb-5">{config.colorKey.heading}</h2>
            {config.colorKey.intro && (
              <p className="max-w-3xl mb-6 font-sweet text-base md:text-lg leading-relaxed text-[hsl(var(--velto-forest))]/90">
                {config.colorKey.intro}
              </p>
            )}
            <div className="bg-white/60 backdrop-blur-sm border border-[hsl(var(--velto-forest))]/10 rounded-lg p-5 md:p-6">
              <ColorKey variant="compact" />
            </div>
            {config.colorKey.note && (
              <p className="mt-4 max-w-3xl text-sm md:text-base font-sweet text-[hsl(var(--velto-forest))]/80">
                {config.colorKey.note}
              </p>
            )}
          </section>
        )}

        {/* Program cards (optional) */}
        {config.programs && (
          <section id="programs" className="container mx-auto px-4 md:px-6 pb-14">
            <h2 className="font-instrument text-3xl md:text-4xl mb-5">{config.programs.heading}</h2>
            {config.programs.intro && (
              <p className="max-w-3xl mb-8 font-sweet text-base md:text-lg leading-relaxed text-[hsl(var(--velto-forest))]/90">
                {config.programs.intro}
              </p>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {config.programs.items.map((p) => {
                const href = withAttribution(p.url);
                return (
                  <article
                    key={p.key}
                    className="bg-white/60 backdrop-blur-sm border border-[hsl(var(--velto-forest))]/10 rounded-lg p-5 flex flex-col"
                  >
                    <h3 className="font-instrument text-xl mb-2">{p.name}</h3>
                    <p className="font-sweet text-[hsl(var(--velto-forest))]/80 text-sm md:text-base leading-relaxed">
                      {p.copy}
                    </p>
                    {p.note && (
                      <p className="mt-2 font-sweet text-sm text-[hsl(var(--velto-forest))]/70">{p.note}</p>
                    )}
                    {p.colors.length > 0 && (
                      <ul className="mt-4 flex flex-wrap gap-2" aria-label={`${p.name} skill groups`}>
                        {p.colors.map((c) => (
                          <li
                            key={c}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--velto-forest))]/15 bg-white/70 px-2.5 py-1 font-sweet text-xs"
                          >
                            <LevelDot color={c} size="sm" />
                            <span>{DOT_COLORS[c].meaning}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        handleCta("program_sessions_click", href, true, { program: p.key })
                      }
                      className="mt-auto pt-4 inline-flex items-center gap-1.5 self-start font-sweet-bold text-sm uppercase tracking-wider text-[hsl(var(--velto-copper))] underline-offset-4 hover:underline"
                    >
                      Find sessions
                      <span className="sr-only">: {p.name} (opens the schedule in a new tab)</span>
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* Bookable league series (optional). One card per day; each skill
            group on each day is its own series with its own registration link. */}
        {config.leagueSeries && (() => {
          const { id, intro, series: s } = config.leagueSeries;
          return (
            <section
              id={id}
              className="container mx-auto px-4 md:px-6 pb-14"
              style={{ scrollMarginTop: "calc(6rem + var(--banner-h, 0px))" }}
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2">
                <LevelDot color={s.color} size="lg" />
                <h2 className="font-instrument text-3xl md:text-4xl">{s.name}</h2>
                <span className="rounded-full border border-[hsl(var(--velto-forest))]/25 px-3 py-1 font-sweet-bold text-xs uppercase tracking-wider">
                  {s.format}
                </span>
              </div>
              <p className="mb-5 font-sweet text-sm uppercase tracking-wider text-[hsl(var(--velto-forest))]/70">
                {DOT_COLORS[s.color].name} · {DOT_COLORS[s.color].meaning}
              </p>
              <div className={s.image ? "grid gap-6 md:grid-cols-2 md:items-center mb-6" : ""}>
                {intro && (
                  <div className="max-w-3xl mb-5 space-y-4 font-sweet text-base md:text-lg leading-relaxed text-[hsl(var(--velto-forest))]/90">
                    {intro.map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                )}
                {s.image && (
                  <img
                    src={s.image.src}
                    srcSet={s.image.srcSet}
                    sizes="(min-width: 768px) 45vw, 100vw"
                    alt={s.image.alt}
                    width={s.image.width}
                    height={s.image.height}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto rounded-xl shadow-lg"
                  />
                )}
              </div>
              <p className="mb-6 font-instrument text-2xl md:text-3xl italic text-[hsl(var(--velto-copper))]">
                {s.days.some((d) => earlyBirdOpen(d))
                  ? `${s.priceEarlyBird} early bird, then ${s.price} ${s.priceNote}`
                  : `${s.price} ${s.priceNote}`}
              </p>
              <div className="grid md:grid-cols-2 gap-5">
                {s.days.map((d) => (
                  <article
                    key={d.day}
                    className="bg-white/70 backdrop-blur-sm border border-[hsl(var(--velto-forest))]/10 rounded-lg p-6 flex flex-col"
                  >
                    <h3 className="font-instrument text-2xl font-semibold mb-3">{d.day}</h3>
                    <dl className="mb-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 font-sweet text-sm md:text-base">
                      <dt className="text-[hsl(var(--velto-forest))]/70">Dates</dt>
                      <dd>
                        {d.dates}, {s.year}
                      </dd>
                      <dt className="text-[hsl(var(--velto-forest))]/70">Time</dt>
                      <dd>{d.time}</dd>
                    </dl>
                    {earlyBirdOpen(d) ? (
                      <p className="mb-6 font-sweet text-sm md:text-base italic text-[hsl(var(--velto-copper))]">
                        Early bird ({s.priceEarlyBird}) ends {d.earlyBirdEnds}
                      </p>
                    ) : (
                      <p className="mb-6 font-sweet text-sm md:text-base text-[hsl(var(--velto-forest))]/70">
                        {s.price} per person
                      </p>
                    )}
                    <div className="mt-auto space-y-3">
                      {d.options.map((o) => {
                        const href = withAttribution(o.url);
                        return (
                          <Button
                            key={o.url}
                            asChild
                            className="w-full min-h-10 h-auto whitespace-normal text-center leading-snug bg-[hsl(var(--velto-copper))] hover:bg-[hsl(var(--velto-copper))]/90 text-white font-sweet-bold uppercase tracking-wider"
                          >
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() =>
                                handleCta("league_register_click", href, true, {
                                  series: s.name,
                                  day: d.day,
                                  level: o.level,
                                })
                              }
                            >
                              Register: {o.level}
                              <span className="sr-only">
                                , {d.day} {s.name} series (opens in a new tab)
                              </span>
                            </a>
                          </Button>
                        );
                      })}
                    </div>
                  </article>
                ))}
              </div>
              <div className="mt-4 max-w-3xl space-y-1 text-sm font-sweet text-[hsl(var(--velto-forest))]/70">
                <p>{s.recommendationNote}</p>
                <p>{s.timezoneNote}</p>
              </div>
            </section>
          );
        })()}

        {/* Long-form content sections (H2 + paragraphs) */}
        {config.sections?.map((s, i) => (
          <section key={i} className="container mx-auto px-4 md:px-6 pb-14">
            <div className="max-w-3xl">
              <h2 className="font-instrument text-3xl md:text-4xl mb-5">{s.heading}</h2>
              <div className="space-y-4 font-sweet text-base md:text-lg leading-relaxed text-[hsl(var(--velto-forest))]/90">
                {s.body.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
            </div>
          </section>
        ))}

        {/* Pricing */}
        {config.pricing && (
          <section className="container mx-auto px-4 md:px-6 pb-14">
            <h2 className="font-instrument text-3xl md:text-4xl mb-6">
              {config.pricing.heading}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
              {config.pricing.items.map((item) => (
                <div
                  key={item.label}
                  className="bg-[hsl(var(--velto-forest))] text-[hsl(var(--velto-beige))] rounded-lg p-5"
                >
                  <div className="font-sweet uppercase tracking-wider text-xs text-[hsl(var(--velto-beige))]/70 mb-1">
                    {item.label}
                  </div>
                  <div className="font-instrument text-3xl text-[hsl(var(--velto-copper))] italic">
                    {item.price}
                  </div>
                  {item.note && (
                    <p className="mt-2 text-sm font-sweet text-[hsl(var(--velto-beige))]/70">
                      {item.note}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {config.pricing.note && (
              <p className="mt-3 text-sm font-sweet text-[hsl(var(--velto-forest))]/70 max-w-3xl">
                {config.pricing.note}
              </p>
            )}
          </section>
        )}

        {/* Membership Plans */}
        {config.plans && (
          <section className="container mx-auto px-4 md:px-6 pb-14">
            <h2 className="font-instrument text-3xl md:text-4xl mb-6">
              {config.plans.heading}
            </h2>
            {/* Full content width, so the cards line up with the feature grid
                above: one row of four on wide screens, 2×2 below that. Never
                three columns — with four packs that strands the last card on a
                row of its own. */}
            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
              {config.plans.items.map((plan) => {
                const href = withAttribution(plan.cta.href);
                const onClick = () => handleCta(plan.cta.eventName, href, plan.cta.external);
                return (
                  <div
                    key={plan.name}
                    className="bg-white/70 backdrop-blur-sm border border-[hsl(var(--velto-forest))]/10 rounded-lg p-6 flex flex-col"
                  >
                    {/* The card's visual anchor now that the button no longer
                        names the pack — a step up in size and weight, same
                        font and colour as before. Four across, most names wrap
                        to two lines, so every title reserves two (2 × the
                        text-3xl line height) and the prices stay on one line. */}
                    <h3 className="font-instrument text-3xl font-semibold mb-2 xl:min-h-[4.5rem]">{plan.name}</h3>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-instrument text-4xl text-[hsl(var(--velto-copper))]">
                        {plan.price}
                      </span>
                      <span className="font-sweet text-sm text-[hsl(var(--velto-forest))]/70">
                        {plan.cadence}{plan.value && ` · ${plan.value}`}
                      </span>
                    </div>
                    <p className="font-sweet italic text-sm text-[hsl(var(--velto-copper))] mb-4">
                      {plan.expiry}
                    </p>
                    <ul className="space-y-2 mb-6 font-sweet text-sm md:text-base text-[hsl(var(--velto-forest))]/85">
                      {plan.includes.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <ChevronRight className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--velto-copper))]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto">
                      {/* h-auto + whitespace-normal is what stops a long label
                          clipping: Button's base sets whitespace-nowrap and
                          size:default pins h-10, so the text had no way to wrap
                          and was cut off at the pill's edge. min-h-10 keeps the
                          original height for a short label like "Grab This Pack".
                          asChild puts these classes (and the accessible name) on
                          the link itself rather than nesting a <button> inside
                          an <a>, so there is exactly one control to announce. */}
                      <Button
                        asChild
                        className="w-full min-h-10 h-auto whitespace-normal text-center leading-snug bg-[hsl(var(--velto-copper))] hover:bg-[hsl(var(--velto-copper))]/90 text-white font-sweet-bold uppercase tracking-wider"
                      >
                        {plan.cta.external ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={plan.cta.ariaLabel}
                            onClick={onClick}
                          >
                            {plan.cta.label}
                          </a>
                        ) : (
                          <Link to={href} aria-label={plan.cta.ariaLabel} onClick={onClick}>
                            {plan.cta.label}
                          </Link>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            {config.plans.note && (
              <p className="mt-4 text-sm font-sweet text-[hsl(var(--velto-forest))]/70 max-w-3xl">
                {config.plans.note}
              </p>
            )}
          </section>
        )}

        {/* Gallery */}
        <section className="container mx-auto px-4 md:px-6 pb-14">
          <div className="grid sm:grid-cols-3 gap-3">
            {config.gallery.map((g) => (
              <img
                key={g.src + g.alt}
                src={g.src}
                srcSet={facilitySrcSet(g.src)}
                sizes="(min-width: 640px) 30vw, calc(100vw - 2rem)"
                alt={g.alt}
                loading="lazy"
                decoding="async"
                width={800}
                height={600}
                className="w-full h-56 object-cover rounded-lg border border-[hsl(var(--velto-forest))]/10"
              />
            ))}
          </div>
        </section>

        {/* Location */}
        <section className="container mx-auto px-4 md:px-6 pb-14">
          <h2 className="font-instrument text-3xl md:text-4xl mb-6">Visit Velto</h2>
          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            <LazyMap />
            <div className="bg-white/60 backdrop-blur-sm border border-[hsl(var(--velto-forest))]/10 rounded-lg p-6 font-sweet">
              <p className="font-instrument text-2xl mb-2">Velto Pickleball Club</p>
              <p className="flex items-start gap-2 mb-2">
                <MapPin className="h-4 w-4 mt-1 shrink-0" /> {ADDRESS_LINE}
              </p>
              <p className="flex items-start gap-2 mb-2">
                <Clock className="h-4 w-4 mt-1 shrink-0" /> Open daily 6:00 AM – 12:00 AM
              </p>
              <p className="flex items-start gap-2 mb-4">
                <Mail className="h-4 w-4 mt-1 shrink-0" />
                <a
                  href="mailto:support@veltopb.com"
                  onClick={() => trackEvent("email_click", { page: config.slug })}
                  className="underline hover:text-[hsl(var(--velto-copper))]"
                >
                  support@veltopb.com
                </a>
              </p>
              <p className="text-sm md:text-base text-[hsl(var(--velto-forest))]/70 mb-4">
                In Red Hook, Brooklyn — easy from Carroll Gardens, Cobble Hill, Park
                Slope, DUMBO, and lower Manhattan. Street parking on Van Brunt; B61 bus
                and IKEA NYC Ferry nearby.
              </p>
              <a
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent("directions_click", { page: config.slug, destination: DIRECTIONS_URL })
                }
              >
                <Button className="bg-[hsl(var(--velto-forest))] hover:bg-[hsl(var(--velto-forest))]/90 text-[hsl(var(--velto-beige))] font-sweet-bold uppercase tracking-wider">
                  Get Directions
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section id="faqs" className="container mx-auto px-4 md:px-6 pb-14">
          <h2 className="font-instrument text-3xl md:text-4xl mb-6">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="max-w-3xl">
            {config.faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-[hsl(var(--velto-forest))]/15"
              >
                <AccordionTrigger className="font-instrument text-lg md:text-xl text-left">
                  {f.question}
                </AccordionTrigger>
                {/* forceMount keeps every answer in the served HTML instead of
                    letting Radix unmount collapsed panels — the prerenderer
                    snapshots the DOM, so unmounted prose never reaches a
                    crawler. Collapsed panels are hidden with display:none by
                    AccordionContent, so they stay out of the tab order and the
                    accessibility tree. */}
                <AccordionContent
                  forceMount
                  className="font-sweet text-[hsl(var(--velto-forest))]/85 text-base md:text-lg leading-relaxed"
                >
                  {f.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Related */}
        <section className="container mx-auto px-4 md:px-6 pb-20">
          <h2 className="font-instrument text-2xl md:text-3xl mb-5">Explore more at Velto</h2>
          <ul className="grid sm:grid-cols-2 gap-3 max-w-3xl">
            {config.relatedLinks.map((l) => (
              <li key={l.href}>
                <Link
                  to={l.href}
                  className="block bg-white/60 hover:bg-white border border-[hsl(var(--velto-forest))]/10 rounded-lg px-4 py-3 font-sweet hover:text-[hsl(var(--velto-copper))] transition-colors"
                >
                  {l.label} <ChevronRight className="inline h-4 w-4 -mt-0.5" />
                </Link>
              </li>
            ))}
          </ul>
        </section>

      </main>

      <Footer />

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[hsl(var(--velto-beige))]/95 backdrop-blur border-t border-[hsl(var(--velto-forest))]/15 p-3">
        {config.primaryCta.external ? (
          <a
            href={primaryHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              handleCta(config.primaryCta.eventName, primaryHref, true, {
                category: "sticky_cta",
                link_text: "sticky_cta",
              })
            }
            className="block"
          >
            <Button className="w-full bg-[hsl(var(--velto-copper))] hover:bg-[hsl(var(--velto-copper))]/90 text-white font-sweet-bold uppercase tracking-wider py-5">
              {config.primaryCta.label}
            </Button>
          </a>
        ) : primaryHref.startsWith("#") ? (
          // In-page anchor (e.g. a league series section): a plain <a> so the
          // browser scrolls to it; a router <Link> would only push the hash.
          <a
            href={primaryHref}
            onClick={() =>
              handleCta(config.primaryCta.eventName, primaryHref, false, {
                category: "sticky_cta",
                link_text: "sticky_cta",
              })
            }
            className="block"
          >
            <Button className="w-full bg-[hsl(var(--velto-copper))] hover:bg-[hsl(var(--velto-copper))]/90 text-white font-sweet-bold uppercase tracking-wider py-5">
              {config.primaryCta.label}
            </Button>
          </a>
        ) : (
          <Link
            to={primaryHref}
            onClick={() =>
              handleCta(config.primaryCta.eventName, primaryHref, false, {
                category: "sticky_cta",
                link_text: "sticky_cta",
              })
            }
            className="block"
          >
            <Button className="w-full bg-[hsl(var(--velto-copper))] hover:bg-[hsl(var(--velto-copper))]/90 text-white font-sweet-bold uppercase tracking-wider py-5">
              {config.primaryCta.label}
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

export default LandingPage;